import { v4 as uuid } from 'uuid';

import { setSendAnswerFailed, setSendAnswerPending, setSendAnswerSuccess } from './actions';
import { trackBubbleInputSend } from './tracking';
import { IWsAnswerEvent } from './types';
import { currentOpenChatIDSelector } from '../../selectors';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TThunkAction } from '../../types/redux/thunk';
import { TSessionSucceedState } from '../../types/session';
import { ETelemetryEvents } from '../../types/telemetry';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { EInternetStatus } from '../internetStatus';

type TSendWsAnswerParams = {
  messageId: string;
  payload: string;
  input?: string;
  analyticsBubbleType?: string;
};

/**
 * Отправка действия по WS
 */
export const sendWsAnswer =
  ({ messageId, payload, input, analyticsBubbleType }: TSendWsAnswerParams): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { telemetry, logger, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const {
      session: { status, token },
      socketStatus,
      internetStatus,
    } = getState();

    const chatId = currentOpenChatIDSelector(getState()) || '';

    dispatch(setSendAnswerPending());

    // в модалке каждый раз когда возвращемся на экран - перезапрашивается сессия
    const isSessionExpired = status !== ERequestApiStatus.Succeed && status !== ERequestApiStatus.Pending;
    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;
    const isOffline = internetStatus === EInternetStatus.Offline;

    const uid = uuid();

    if (isSocketIsClose) {
      dispatch(setSocketStatusFailed());
    }

    if (isSessionExpired || isSocketIsClose || isOffline) {
      clientEvents.set(uid, { type: EWsClientEventType.Answer, status: ERequestApiStatus.Failed });
      dispatch(setSendAnswerFailed());

      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendFail);
      logger.warning('Не удалось отправить действие по ws', {
        domain: 'frontend-chats.sendWsAnswer.ts',
        message: 'Сессия истекла или соединение закрыто',
      });

      return;
    }

    const wsAnswer: IWsAnswerEvent = {
      type: EWsClientEventType.Answer,
      uid,
      message: {
        messageId,
        payload,
        ...(input && { input }),
      },
      userId: (token as TSessionSucceedState['token']).userId,
      sessionToken: (token as TSessionSucceedState['token']).sessionToken,
    };

    clientEvents.set(uid, {
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Answer,
    });

    try {
      await timeoutPromise(eventChannel.sendAnswer(wsAnswer), 10000);
      dispatch(setSendAnswerSuccess());
      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendSuccess);

      if (input && analyticsBubbleType) {
        trackBubbleInputSend(chatId, analyticsBubbleType);
      }
    } catch (err) {
      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendFail);

      logger.warning(err, {
        domain: 'frontend-chats.sendWsAnswer.ts',
        message: 'Не удалось отправить действие по ws',
      });

      clientEvents.set(uid, { type: EWsClientEventType.Answer, status: ERequestApiStatus.Failed });
      dispatch(setSendAnswerFailed());
    }
  };
